import React, { useState, useEffect } from 'react';
import {
  ClockIcon, UserIcon, DocumentTextIcon, ExclamationTriangleIcon,
  InformationCircleIcon, CheckCircleIcon, XCircleIcon, ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { useQuery, useMutation } from '@tanstack/react-query';
import apiService from '../services/apiService';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import Badge, { BadgeTone } from '../components/ui/Badge';
import Button from '../components/ui/Button';

const SEVERITY_TONE: Record<string, BadgeTone> = {
  critical: 'danger',
  high: 'danger',
  medium: 'warning',
  low: 'success',
};

interface AuditEvent {
  id: string;
  userId: string;
  userName: string;
  action: string;
  resource: string;
  resourceId: string;
  details: string;
  ipAddress: string;
  userAgent: string;
  location: string;
  timestamp: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

interface AuditSummary {
  totalEvents: number;
  eventsByUser: { userId: string; userName: string; count: number }[];
  eventsByAction: { action: string; count: number }[];
  eventsBySeverity: { severity: string; count: number }[];
  recentActivity: AuditEvent[];
}

const AuditTrail: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState<'24h' | '7d' | '30d' | '90d'>('7d');
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [selectedAction, setSelectedAction] = useState<string>('all');

  // Fetch audit trail data
  const { data: auditTrail, isLoading, error } = useQuery({
    queryKey: ['audit-trail', selectedTimeframe, selectedUser, selectedAction],
    queryFn: async () => {
      const params = new URLSearchParams({
        timeframe: selectedTimeframe,
        ...(selectedUser !== 'all' && { userId: selectedUser }),
        ...(selectedAction !== 'all' && { action: selectedAction })
      });
      const response = await apiService.get(`/audit-trail?${params}`);
      return response.data;
    }
  });

  // Fetch audit summary
  const { data: summary } = useQuery({
    queryKey: ['audit-summary', selectedTimeframe],
    queryFn: async () => {
      const response = await apiService.get(`/audit-trail/summary?timeframe=${selectedTimeframe}`);
      return response.data;
    }
  });

  // Export audit trail
  const exportMutation = useMutation({
    mutationFn: async (format: 'csv' | 'json') => {
      const params = new URLSearchParams({
        timeframe: selectedTimeframe,
        format,
        ...(selectedUser !== 'all' && { userId: selectedUser }),
        ...(selectedAction !== 'all' && { action: selectedAction })
      });
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1'}/audit-trail/export?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      return response.blob();
    },
    onSuccess: (blob, format) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-trail-${selectedTimeframe}-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  });

  const getActionIcon = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create': return <DocumentTextIcon className="h-4 w-4" />;
      case 'update': return <CheckCircleIcon className="h-4 w-4" />;
      case 'delete': return <XCircleIcon className="h-4 w-4" />;
      case 'login': return <UserIcon className="h-4 w-4" />;
      case 'logout': return <UserIcon className="h-4 w-4" />;
      default: return <InformationCircleIcon className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading audit trail</h3>
              <div className="mt-2 text-sm text-red-700">
                {error instanceof Error ? error.message : 'Unknown error occurred'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Audit Trail" subtitle="Track and monitor all system activities and user actions" />

      {/* Filters and Export */}
      <div className="mb-6 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex flex-wrap gap-4">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>

          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Users</option>
            {/* Add user options here */}
          </select>

          <select
            value={selectedAction}
            onChange={(e) => setSelectedAction(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Actions</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
            <option value="login">Login</option>
            <option value="logout">Logout</option>
          </select>
        </div>

        <div className="flex gap-2">
          <Button variant="secondary" icon={ArrowDownTrayIcon} disabled={exportMutation.isPending} onClick={() => exportMutation.mutate('csv')}>
            Export CSV
          </Button>
          <Button variant="secondary" icon={ArrowDownTrayIcon} disabled={exportMutation.isPending} onClick={() => exportMutation.mutate('json')}>
            Export JSON
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Events" value={summary.totalEvents} icon={ClockIcon} tone="blue" />
          <StatCard label="Active Users" value={summary.eventsByUser.length} icon={UserIcon} tone="emerald" />
          <StatCard label="Action Types" value={summary.eventsByAction.length} icon={DocumentTextIcon} tone="amber" />
          <StatCard
            label="High Severity"
            value={summary.eventsBySeverity.find((s: { severity: string; count: number }) => s.severity === 'high')?.count || 0}
            icon={ExclamationTriangleIcon}
            tone="red"
          />
        </div>
      )}

      {/* Audit Events Table */}
      <div className="rounded-sm border border-gray-300 bg-white overflow-hidden dark:border-gray-700 dark:bg-gray-800">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Recent Activity</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Resource
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Severity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700">
              {auditTrail?.data?.map((event: AuditEvent) => (
                <tr key={event.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getActionIcon(event.action)}
                      <span className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-100">{event.action}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{event.userName}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{event.userId}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{event.resource}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{event.resourceId}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge tone={SEVERITY_TONE[event.severity] ?? 'neutral'}>{event.severity}</Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(event.timestamp).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 max-w-xs truncate">
                    {event.details}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AuditTrail;
